/**
 * Song publishes: a song (its master, cover and facts) goes to an audio store. SoundCloud first
 * (owner decision 2026-10-15, spec `docs/superpowers/specs/2026-10-15-music24-soundcloud-connector-design.md`,
 * design `soundcloud-connector/` c1-c7).
 *
 * The records live in the same table as the kit one-off publishes (`AssetPublishes`, with
 * `catalogItemId` and no `kitAssetId`), but they have their own routes and answer: the native apps
 * decode `SerializedAssetPublish` with a required kit file, and they do not know these channels.
 *
 * The web app, the API and the tests read this file. No imports but the channel rules.
 */
import type { AssetPublishStatus, PublishTiming, SongPublishChannel } from './oneOffPublish';

/** `public` or `private` on the store. */
export const SONG_PUBLISH_SHARING = ['public', 'private'] as const;
export type SongPublishSharing = (typeof SONG_PUBLISH_SHARING)[number];

export function isSongPublishSharing(value: unknown): value is SongPublishSharing {
  return typeof value === 'string' && (SONG_PUBLISH_SHARING as readonly string[]).includes(value);
}

/** Why a song record failed or left the store. Shown with its own sentence (design c6). */
export type SongPublishFailReason =
  /** SoundCloud refused the upload (a 4xx that is not 401, e.g. the upload time is full). */
  | 'refused'
  /** The login ended: Reconnect. */
  | 'reconnect'
  /** The upload broke after the file went out: check SoundCloud before you try again. */
  | 'unsure'
  /** The master failed or is gone. */
  | 'master'
  /** Any other error; the job tried 3 times. */
  | 'error';

/** The removed reasons of a song record. `private`: a public track that someone made private. */
export type SongPublishRemovedReason = 'deleted' | 'private' | 'canceled';

/** The error codes of the SoundCloud connector (`cause.code` of a REST error, `data.errorType` of tRPC). */
export const SOUNDCLOUD_ERROR_CODES = {
  /**
   * The workspace has no `canUseAutoUpload`. The song upload no longer sends it (owner decision
   * 2026-10-18); the clients keep the code for an older server.
   */
  autoUploadNotEnabled: 'M24_AUTO_UPLOAD_NOT_ENABLED',
  /** The SoundCloud login is connected to another Music24 artist. */
  loginInUse: 'M24_SOUNDCLOUD_LOGIN_IN_USE',
  /** The sign-in link is unknown, used or expired. */
  connectStateInvalid: 'M24_CONNECT_STATE_INVALID',
  /** The song has a live record on this channel. */
  alreadyLive: 'M24_PUBLISH_ALREADY_LIVE',
  /** The artist has no SoundCloud connection, or it needs a Reconnect. */
  notConnected: 'M24_PUBLISH_NOT_CONNECTED',
  /** The second sign-in (SoundCloud Insights) needs the official login with an artist first. */
  webSessionNeedsConnection: 'M24_WEB_SESSION_NEEDS_CONNECTION',
  /** The second sign-in is another SoundCloud user than the official login. */
  webSessionOtherUser: 'M24_SOUNDCLOUD_WEB_SESSION_OTHER_USER',
} as const;

export interface SerializedSongPublish {
  uuid: string;
  catalogItemUuid: string;
  channel: SongPublishChannel;
  status: AssetPublishStatus;
  timing: PublishTiming;
  /** ISO time Music24 uploads the song (waiting), or null for "now". */
  publishAt: string | null;
  /** ISO time the upload finished. */
  publishedAt: string | null;
  /** The store id (SoundCloud urn). */
  externalId: string | null;
  /** The track page on the store. */
  externalUrl: string | null;
  title: string | null;
  /** The sharing Music24 sent. The poll compares the store with it. */
  sharing: SongPublishSharing;
  failReason: SongPublishFailReason | null;
  /** The text of the store, for `refused` and `error`. */
  failDetail: string | null;
  removedReason: SongPublishRemovedReason | null;
  createdAt: string;
  updatedAt: string | null;
}

/** The store login that uploads the song (the artist-portal connection). */
export interface SongPublishConnection {
  connected: boolean;
  needsReconnect: boolean;
  displayName: string | null;
  avatarUrl: string | null;
  profileUrl: string | null;
}

/** What the song brings to the store, read only in the dialog (design c3, c4). */
export interface SongPublishFacts {
  title: string;
  artistName: string;
  /** The master: its file name and size, or the reason it cannot go. */
  master:
    | { state: 'ready'; fileName: string; sizeBytes: number; durationSec: number | null }
    | { state: 'missing' | 'processing' | 'failed' };
  coverUrl: string | null;
  isrc: string | null;
  /** YYYY-MM-DD, or null. */
  releaseDate: string | null;
  releaseTitle: string | null;
  smartLinkUrl: string | null;
}

/** `GET /accounts/:accountId/song-publishes/defaults?catalogItemUuid=&channel=soundcloud` */
export interface SongPublishDefaultsResponse {
  catalogItemUuid: string;
  channel: SongPublishChannel;
  connection: SongPublishConnection;
  facts: SongPublishFacts;
  /** The form values: title = song title, description = "Listen everywhere: <SmartLink>". */
  title: string;
  description: string;
  sharing: SongPublishSharing;
  /** The newest record of the song on this channel, or null. */
  current: SerializedSongPublish | null;
}

/** `GET /accounts/:accountId/song-publishes[?catalogItemUuid=]` */
export interface SongPublishListResponse {
  publishes: SerializedSongPublish[];
}

/** `POST /accounts/:accountId/song-publishes` */
export interface CreateSongPublishBody {
  catalogItemUuid: string;
  channel: SongPublishChannel;
  timing: PublishTiming;
  /** ISO time. Required for `later`; for `releaseDay` the start of the release day in the user's zone. */
  publishAt?: string | null;
  title: string;
  description?: string;
  sharing: SongPublishSharing;
}

/** `PATCH /accounts/:accountId/song-publishes/:publishUuid` (a waiting record only). */
export interface UpdateSongPublishBody {
  timing: PublishTiming;
  publishAt?: string | null;
}

/** `{ publish }` of POST, PATCH and DELETE. */
export interface SongPublishResponse {
  publish: SerializedSongPublish | null;
}

/** SoundCloud limits of the form (the store cuts longer text). */
export const SOUNDCLOUD_TITLE_MAX_LENGTH = 100;
export const SOUNDCLOUD_DESCRIPTION_MAX_LENGTH = 4000;

export function defaultSongPublishDescription(smartLinkUrl: string | null): string {
  return smartLinkUrl === null ? '' : `Listen everywhere: ${smartLinkUrl}`;
}

/**
 * The badge of a song on a store (design c1, c7): `none` dashed "Not on SoundCloud", `working`
 * violet (waits or uploads), `live` green (public or private as sent), `bad` red (failed or removed).
 */
export type SongPublishBadge = 'none' | 'working' | 'live' | 'bad';

export function songPublishBadge(record: SerializedSongPublish | null | undefined): SongPublishBadge {
  if (record === null || record === undefined) {
    return 'none';
  }
  switch (record.status) {
    case 'waiting':
    case 'scheduled':
    case 'uploading':
      return 'working';
    case 'published':
      return 'live';
    case 'canceled':
      return 'none';
    case 'failed':
    case 'removed':
      return 'bad';
  }
}

/** A new publish may start: no record, or the last one is not live and not on its way. */
export function canStartSongPublish(record: SerializedSongPublish | null | undefined): boolean {
  const badge = songPublishBadge(record);
  return badge === 'none' || badge === 'bad';
}
